import { useCallback, useContext, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { oneDark } from '@codemirror/theme-one-dark';
import { PyodideContext } from './PyodideContext';
import { runCell, getNamespace } from '../../hooks/usePyodide';

async function fetchErrorExplanation(code, errorText, onChunk) {
  try {
    const res = await fetch('/api/ai/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: 'In one short sentence for a Python beginner, explain what caused this error and how to fix it.',
        context: {
          subject: 'Python Programming',
          grade: '',
          moduleTitle: 'Python Lesson',
          submoduleTitle: '',
          blockHeading: 'Error explanation',
          blockContent: `Code:\n${code}\n\nError:\n${errorText}`,
          firstName: '',
        },
        history: [],
      }),
    });
    if (!res.ok) return;
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop();
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const payload = line.slice(6).trim();
        if (payload === '[DONE]') return;
        try {
          const parsed = JSON.parse(payload);
          if (parsed.text) onChunk(parsed.text);
        } catch { /* ignore malformed line */ }
      }
    }
  } catch { /* network errors are silent for the explainer */ }
}

export default function CodeCell({ cell, onRun }) {
  const { pyodide } = useContext(PyodideContext);
  const [code, setCode] = useState(cell.starter || '');
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [explaining, setExplaining] = useState(false);

  const handleRun = useCallback(async () => {
    if (!pyodide || running) return;
    setRunning(true);
    setOutput(null);
    setExplanation('');

    try {
      const result = await runCell(pyodide, code);
      setOutput(result);

      const namespace = await getNamespace(pyodide);
      onRun?.({ heading: cell.heading || '', code, error: result.error, stdout: result.stdout, stderr: result.stderr, namespace });

      if (result.error) {
        setExplaining(true);
        fetchErrorExplanation(code, result.error, (chunk) => {
          setExplanation((prev) => prev + chunk);
        }).finally(() => setExplaining(false));
      }
    } catch (err) {
      setOutput({ stdout: '', stderr: '', error: err.message });
    } finally {
      setRunning(false);
    }
  }, [pyodide, code, running, cell.heading, onRun]);

  const hasOutput = output !== null;
  const hasError = output?.error;

  return (
    <div className={`pylab-code-cell${hasError ? ' pylab-code-cell--error' : ''}`}>
      {cell.heading && <p className="pylab-cell-label">{cell.heading}</p>}

      <div className="pylab-editor-wrap">
        <CodeMirror
          value={code}
          extensions={[python()]}
          theme={oneDark}
          onChange={setCode}
          editable={cell.editable !== false}
          basicSetup={{ lineNumbers: true, foldGutter: false, autocompletion: true, highlightActiveLine: true }}
        />
      </div>

      <div className="pylab-cell-actions">
        <button
          type="button"
          className="pylab-run-btn"
          onClick={handleRun}
          disabled={!pyodide || running}
        >
          {running
            ? <><span className="pylab-spinner" aria-hidden="true" /> Running…</>
            : <><span aria-hidden="true">▶</span> Run</>}
        </button>
        {hasOutput && (
          <button type="button" className="pylab-clear-btn" onClick={() => { setOutput(null); setExplanation(''); }}>
            Clear
          </button>
        )}
      </div>

      {hasOutput && (
        <div className="pylab-output">
          {output.stdout && <pre className="pylab-stdout">{output.stdout}</pre>}
          {output.stderr && <pre className="pylab-stderr">{output.stderr}</pre>}
          {output.error && <pre className="pylab-error">{output.error}</pre>}
          {!output.stdout && !output.stderr && !output.error && (
            <span className="pylab-output-done">Done — no output</span>
          )}
          {(explaining || explanation) && (
            <div className="pylab-explainer">
              <span className="pylab-explainer-icon" aria-hidden="true">💡</span>
              <span className="pylab-explainer-text">
                {explanation || <span className="pylab-explainer-thinking">Thinking…</span>}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
